import type { Passkey } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, ScanFace, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { date, relative } from '../lib/format';
import {
  biometryName,
  dismissOffer,
  enableLabel,
  isCancel,
  offerDismissedRecently,
  passkeyError,
  platformAvailable,
  registerPasskey,
} from '../lib/passkeys';
import { meQuery, useMe } from '../lib/session';
import { Button, IconButton } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { EmptyState, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Panel } from '../ui/layout';

const passkeysQuery = { queryKey: ['passkeys'], queryFn: () => api<Passkey[]>('/auth/passkeys') };

function BioIcon({ className }: { className?: string }) {
  const Icon = biometryName() === 'Face ID' ? ScanFace : Fingerprint;
  return <Icon className={className} aria-hidden />;
}

function useEnable(onDone?: () => void) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const m = useMutation({
    mutationFn: registerPasskey,
    onMutate: () => setError(null),
    onSuccess: async () => {
      toast(`Вход по ${biometryName()} включён на этом устройстве`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['passkeys'] }),
        qc.invalidateQueries({ queryKey: meQuery.queryKey }),
      ]);
      onDone?.();
    },
    onError: (err) => {
      if (!isCancel(err)) setError(passkeyError(err));
    },
  });
  return { enable: () => m.mutate(), busy: m.isPending, error };
}

/**
 * Предложение после входа по паролю: «Входить по Face ID?» — один раз на устройстве,
 * только если у пользователя ещё нет ни одного ключа и устройство умеет биометрию.
 */
export function PasskeyOffer() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const { enable, busy, error } = useEnable(() => setOpen(false));

  useEffect(() => {
    // Демо-пользователю ключ входа не нужен: сессия на два часа, аккаунт общий
    if (me.user.hasPasskey || me.user.demoRole || offerDismissedRecently()) return;
    let alive = true;
    const t = setTimeout(() => {
      platformAvailable().then((ok) => {
        if (alive && ok) setOpen(true);
      });
    }, 1200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [me.user.hasPasskey, me.user.demoRole]);

  const name = biometryName();
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) dismissOffer();
        setOpen(v);
      }}
      width="sm"
      title={`Входить по ${name}?`}
      description="Пароль больше не понадобится на этом устройстве. Он продолжит работать на других."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              dismissOffer();
              setOpen(false);
            }}
          >
            Не сейчас
          </Button>
          <Button variant="primary" loading={busy} icon={<BioIcon className="size-4" />} onClick={enable}>
            {enableLabel()}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-4">
        <span className="visor grid size-14 shrink-0 place-items-center text-ink">
          <BioIcon className="size-8" />
        </span>
        <p className="text-[14px] leading-6 text-ink-2">
          Портал создаст ключ входа и сохранит его в защищённом хранилище устройства. Биометрия остаётся на
          устройстве — портал её не получает.
        </p>
      </div>
      <ErrorBox error={error} className="mt-4" />
    </Dialog>
  );
}

/** Профиль: ключи входа по биометрии и подключение текущего устройства */
export function PasskeysPanel() {
  const qc = useQueryClient();
  const list = useQuery(passkeysQuery);
  const [available, setAvailable] = useState(false);
  const { enable, busy, error } = useEnable();
  const remove = useMutation({
    mutationFn: (id: string) => api(`/auth/passkeys/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast('Ключ входа удалён');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['passkeys'] }),
        qc.invalidateQueries({ queryKey: meQuery.queryKey }),
      ]);
    },
  });

  useEffect(() => {
    platformAvailable().then(setAvailable);
  }, []);

  const name = biometryName();
  return (
    <Panel
      title="Вход по Face ID и отпечатку"
      actions={
        available ? (
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            icon={<BioIcon className="size-4" />}
            onClick={enable}
          >
            Добавить это устройство
          </Button>
        ) : null
      }
    >
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : list.data.length === 0 ? (
        <EmptyState title="Вход по биометрии не включён">
          {available
            ? `Добавьте это устройство — и входите по ${name} без пароля.`
            : 'Откройте портал на телефоне или ноутбуке с Face ID, Touch ID или Windows Hello и добавьте устройство здесь.'}
        </EmptyState>
      ) : (
        <ul className="divide-y divide-line">
          {list.data.map((k) => (
            <li key={k.id} className="flex items-center gap-3 py-3">
              <BioIcon className="size-5 shrink-0 text-ink-2" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium text-ink">{k.name}</p>
                <p className="text-[12.5px] text-ink-2">
                  Добавлен {date(k.createdAt)}
                  {k.lastUsedAt ? `, последний вход ${relative(k.lastUsedAt)}` : ', ещё не использовался'}
                  {k.deviceType === 'multiDevice' ? '. Доступен на всех устройствах этой учётной записи' : ''}
                </p>
              </div>
              <IconButton
                label="Удалить ключ"
                onClick={() => remove.mutate(k.id)}
                disabled={remove.isPending}
              >
                <Trash2 className="size-4" />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      <ErrorBox error={error ?? remove.error} className="mt-3" />
    </Panel>
  );
}
